/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { isEmpty } from 'lodash';

import {
    createFeatureSelector,
    createSelector,
} from '@ngrx/store';

import {
    LicenseTierType,
    SiteMemberType,
} from 'generated-types';

import { ISystemInfoState } from './system-info.state';

export const systemInfoFeatureKey = 'systemInfo';

export const selectSystemInfoFeature =
    createFeatureSelector<ISystemInfoState>(systemInfoFeatureKey);

// --------------- Individual selectors ---------------

export const selectLoaded = createSelector(
    selectSystemInfoFeature,
    ({ loaded }) => loaded,
);

export const selectLoading = createSelector(
    selectSystemInfoFeature,
    ({ loading }) => loading,
);

export const selectLoadingError = createSelector(
    selectSystemInfoFeature,
    ({ loadingError }) => loadingError,
);

export const selectAllowReload = createSelector(
    selectSystemInfoFeature,
    ({ allowReload }) => allowReload,
);

export const selectSystemConfiguration = createSelector(
    selectSystemInfoFeature,
    ({ systemconfiguration }) => systemconfiguration,
);

export const selectIsAwsCloud = createSelector(
    selectSystemInfoFeature,
    (systemInfo: ISystemInfoState) => systemInfo.cluster?.name,
);

export const selectDefaultLicenseTier = createSelector(
    selectSystemInfoFeature,
    ({ systemconfiguration }) => systemconfiguration?.default_license_tier,
);

export const selectIsEssentialsLicense = createSelector(
    selectDefaultLicenseTier,
    defaultLicenseTier => defaultLicenseTier === LicenseTierType.ESSENTIALS,
);

export const selectIsEnterpriseOrEnterpriseWithCloudServicesTier = createSelector(
    selectDefaultLicenseTier,
    defaultLicenseTier => defaultLicenseTier === LicenseTierType.ENTERPRISE ||
        defaultLicenseTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES,
);

export const selectGslbConfig = createSelector(
    selectSystemInfoFeature,
    ({ gslb }) => gslb,
);

export const selectHasGslbConfig = createSelector(
    selectGslbConfig,
    // gslb can be an empty object
    gslb => !isEmpty(gslb),
);

export const selectGslbId = createSelector(
    selectGslbConfig,
    gslb => gslb?.uuid,
);

export const selectLocalCluster = createSelector(
    selectSystemInfoFeature,
    ({ cluster }) => cluster,
);

export const selectLocalClusterId = createSelector(
    selectLocalCluster,
    cluster => cluster?.uuid,
);

export const selectSeInProviderContext = createSelector(
    selectSystemInfoFeature,
    ({ systemconfiguration }) => systemconfiguration?.global_tenant_config?.se_in_provider_context,
);

// --------------- Combined selectors ---------------

/**
 * Compares GSLB config leader cluster_id of with cluster_id of a current controller.
 * Used by GSLB related Items and Collections to figure out whether they are changeable.
 */
export const selectLocalSiteIsGslbLeader = createSelector(
    selectGslbConfig,
    selectLocalClusterId,
    (gslb, id) => gslb?.leader_cluster_uuid === id,
);

export const selectedLeaderClusterId = createSelector(
    selectGslbConfig,
    gslb => gslb?.leader_cluster_uuid,
);

/**
 * Select an IGslbSite object with member_type joined.
 */
export const selectLocalGslbSiteConfig = createSelector(
    selectLocalCluster,
    selectSystemConfiguration,
    (cluster, sysConfig) => {
        return {
            cluster_uuid: cluster.uuid,
            ip_addresses: cluster.nodes.map(node => node.ip),
            port: sysConfig.portal_configuration.port,
            member_type: SiteMemberType.GSLB_ACTIVE_MEMBER,
        };
    },
);
